import {
  STAFF_CATALOG_BOUNDS,
  type StaffCatalogItemRecord,
  type StaffCatalogLocationConfirmBody,
  type StaffCatalogLocationSource,
  type StaffCatalogPlaceView,
  staffCatalogLocationConfirmBodySchema,
  staffCatalogLocationSources,
} from "@lafayette/schemas";

type StaffCatalogLocationStatus = StaffCatalogPlaceView["location"];

import { isValidTimeZone } from "../time";
import type { FieldErrors } from "../validation";

// The form of the location confirmation (P6). Its values come from the item's own saved columns and from
// what a staff member types. They never come from a place search: the search panel has no way to put a
// value here. A group is a value or "unknown"; nothing is "left as it was".

export type LocationValues = Readonly<{
  pointUnknown: boolean;
  latitude: string;
  longitude: string;
  placeUnknown: boolean;
  locality: string;
  region: string;
  countryCode: string;
  addressText: string;
  timeZoneUnknown: boolean;
  timeZone: string;
  /** `""` until a source is chosen. A source is needed when any group has a value. */
  source: StaffCatalogLocationSource | "";
}>;

/** The fields in page order, for the error summary and the focus on the first error. */
export const locationFieldOrder = [
  "latitude",
  "longitude",
  "locality",
  "region",
  "countryCode",
  "addressText",
  "timeZone",
  "source",
] as const;
export type LocationFieldKey = (typeof locationFieldOrder)[number];

// The Details section has fields with the plain names (Latitude, Locality ...). These are "Confirmed ...",
// so no two fields on the item page share an accessible name.
export const locationFieldLabels: Readonly<Record<LocationFieldKey, string>> = {
  latitude: "Confirmed latitude",
  longitude: "Confirmed longitude",
  locality: "Confirmed locality",
  region: "Confirmed region",
  countryCode: "Confirmed country code",
  addressText: "Confirmed address",
  timeZone: "Confirmed time zone",
  source: "Where the values come from",
};

const text = (value: string | null | undefined) => value ?? "";
const numberText = (value: number | null) => (value === null ? "" : String(value));

/**
 * The start of the form: the item's saved values, and "unknown" for a group a staff member recorded as
 * unknown. An empty group nobody assessed starts empty, so the form asks for a value or "unknown".
 */
export function locationValuesFromItem(
  item: StaffCatalogItemRecord,
  status: StaffCatalogLocationStatus,
): LocationValues {
  const source = status.source === null || status.source === "NONE" ? "" : status.source;
  return {
    pointUnknown: status.point === "UNKNOWN",
    latitude: numberText(item.latitude),
    longitude: numberText(item.longitude),
    placeUnknown: status.place === "UNKNOWN",
    locality: text(item.locality),
    region: text(item.region),
    countryCode: text(item.countryCode),
    addressText: text(item.addressText),
    timeZoneUnknown: status.timeZone === "UNKNOWN",
    timeZone: text(item.timeZone),
    source,
  };
}

const DECIMAL = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

function parseCoordinate(
  raw: string,
  limit: number,
  empty: string,
  range: string,
): { value: number } | { error: string } {
  const trimmed = raw.trim();
  if (trimmed === "") return { error: empty };
  if (!DECIMAL.test(trimmed)) return { error: "Enter a number, for example 36.7213." };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || Math.abs(value) > limit) return { error: range };
  return { value };
}

/** True when at least one group has a value (the source is then required). */
export function hasAnyValue(values: LocationValues): boolean {
  return !values.pointUnknown || !values.placeUnknown || !values.timeZoneUnknown;
}

export type LocationMapResult =
  | Readonly<{ ok: true; body: StaffCatalogLocationConfirmBody }>
  | Readonly<{ ok: false; errors: FieldErrors }>;

/**
 * Checks the form and builds the P6 body. The checks repeat the schema's, with sentences a staff member
 * can act on; the schema runs last as a backstop, so a body the server would refuse never leaves the form.
 */
export function locationValuesToBody(
  values: LocationValues,
  expectedVersion: number,
): LocationMapResult {
  const errors: Record<string, string> = {};

  let point: StaffCatalogLocationConfirmBody["point"] = "UNKNOWN";
  if (!values.pointUnknown) {
    const latitude = parseCoordinate(
      values.latitude,
      90,
      "Enter the latitude, or mark the coordinates as unknown.",
      "Latitude is between -90 and 90.",
    );
    const longitude = parseCoordinate(
      values.longitude,
      180,
      "Enter the longitude, or mark the coordinates as unknown.",
      "Longitude is between -180 and 180.",
    );
    if ("error" in latitude) errors.latitude = latitude.error;
    if ("error" in longitude) errors.longitude = longitude.error;
    if ("value" in latitude && "value" in longitude) {
      point = { latitude: latitude.value, longitude: longitude.value };
    }
  }

  let place: StaffCatalogLocationConfirmBody["place"] = "UNKNOWN";
  if (!values.placeUnknown) {
    const locality = values.locality.trim();
    const region = values.region.trim();
    const countryCode = values.countryCode.trim().toUpperCase();
    const addressText = values.addressText.trim();
    if (locality === "") {
      errors.locality = "Enter the locality, or mark the place details as unknown.";
    } else if (locality.length > STAFF_CATALOG_BOUNDS.locality) {
      errors.locality = `Use at most ${STAFF_CATALOG_BOUNDS.locality} characters.`;
    }
    if (countryCode === "") {
      errors.countryCode = "Enter the 2-letter country code, for example ES.";
    } else if (!/^[A-Z]{2}$/.test(countryCode)) {
      errors.countryCode = "Use 2 letters, for example ES.";
    }
    if (region.length > STAFF_CATALOG_BOUNDS.region) {
      errors.region = `Use at most ${STAFF_CATALOG_BOUNDS.region} characters.`;
    }
    if (addressText.length > STAFF_CATALOG_BOUNDS.addressText) {
      errors.addressText = `Use at most ${STAFF_CATALOG_BOUNDS.addressText} characters.`;
    }
    if (
      errors.locality === undefined &&
      errors.countryCode === undefined &&
      errors.region === undefined &&
      errors.addressText === undefined
    ) {
      place = {
        locality,
        region: region === "" ? null : region,
        countryCode,
        addressText: addressText === "" ? null : addressText,
      };
    }
  }

  let timeZone = "UNKNOWN";
  if (!values.timeZoneUnknown) {
    const zone = values.timeZone.trim();
    if (zone === "") {
      errors.timeZone = "Choose the time zone, or mark it as unknown.";
    } else if (zone.length > 64 || !isValidTimeZone(zone)) {
      errors.timeZone = "Enter a valid time zone, for example Europe/Madrid.";
    } else {
      timeZone = zone;
    }
  }

  const anyValue = hasAnyValue(values);
  let source: StaffCatalogLocationSource = "NONE";
  if (anyValue) {
    if (values.source === "" || !staffCatalogLocationSources.includes(values.source)) {
      errors.source = "Choose where these values come from. It cannot be Google.";
    } else {
      source = values.source;
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const body: StaffCatalogLocationConfirmBody = {
    expectedVersion,
    point,
    place,
    timeZone,
    source,
  };
  const checked = staffCatalogLocationConfirmBodySchema.safeParse(body);
  if (!checked.success) {
    return {
      ok: false,
      errors: { "": "The server would not accept these values. Check the form." },
    };
  }
  return { ok: true, body };
}
